"use client";

import Link from "next/link";
import { useState } from "react";
import { buildExploreTagHrefForTag } from "../_lib/explore-detail-hrefs";
import { useExploreRoute } from "../_lib/explore-route-context";
import type { TrendingTag } from "../types";

interface TrendingTagsSectionProps {
  tags: TrendingTag[];
  initialVisible?: number;
}

export function TrendingTagsSection({
  tags,
  initialVisible = 18,
}: TrendingTagsSectionProps) {
  const { exploreBase } = useExploreRoute();
  const [showAll, setShowAll] = useState(false);

  const hasMore = tags.length > initialVisible;
  const visibleTags = showAll ? tags : tags.slice(0, initialVisible);

  return (
    <div className="rounded-2xl border border-[var(--explore-border-light)] bg-[var(--explore-bg-card)] p-6 shadow-[var(--explore-shadow-card)]">
      <div className="mb-4 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-bold text-[1.1rem] text-[var(--explore-text-primary)]">
          🔥 Most covered this week
        </h2>
        {hasMore && (
          <button
            className="flex items-center gap-1 font-medium text-[0.85rem] text-[var(--explore-accent-teal)] hover:underline"
            onClick={() => setShowAll((prev) => !prev)}
            type="button"
          >
            {showAll ? "Show less" : "Show all"}
            <svg
              className={`transition-transform ${showAll ? "rotate-180" : ""}`}
              fill="none"
              height="14"
              stroke="currentColor"
              strokeWidth="2"
              viewBox="0 0 24 24"
              width="14"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2.5">
        {visibleTags.map((tag) => (
          <Link
            className={`hover:-translate-y-0.5 flex items-center gap-1.5 rounded-sm px-4 py-2 font-medium text-[0.9rem] no-underline transition-all hover:opacity-80 ${
              tag.isHot
                ? "font-semibold text-[var(--explore-text-primary)]"
                : "bg-[var(--explore-tag-bg)] text-[var(--explore-tag-text)]"
            }`}
            href={buildExploreTagHrefForTag({ exploreBase, tag })}
            key={tag.slug}
            style={
              tag.isHot
                ? {
                    background:
                      "linear-gradient(to bottom right, var(--explore-trending-hot-from), var(--explore-trending-hot-to))",
                  }
                : undefined
            }
          >
            {tag.displayName}
            <span
              className={`rounded-full px-1.5 py-0.5 text-[0.75rem] ${
                tag.isHot
                  ? "bg-[var(--explore-trending-hot-count-bg)] text-[var(--explore-text-secondary)]"
                  : "bg-[var(--explore-trending-count-bg)] text-[var(--explore-text-muted)]"
              }`}
            >
              {tag.count}
            </span>
          </Link>
        ))}
      </div>
    </div>
  );
}
